import React, { useEffect } from "react";
import logging from "utils/logging";
import { Typography, Fade, keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import FinalCategoryCell from "./FinalCategoryCell";
import { useSelector, useDispatch } from "react-redux";
import {
  selectGameId,
  selectGameObject,
  selectGameStatusObject,
} from "../../gamestate/gameStateSlice";
import { selectAllPlayers } from "../../player/playerSlice";
import {
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_QUESTION_WAGERING,
  GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW,
  GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS,
} from "../../gamestate/constants";
import { selectCurrentQuestionObject } from "../../question/questionSlice";
import {
  watchQuestionSubmissionsAction,
  stopWatchingQuestionSubmissionsAction,
} from "../../question/submissionSlice";
import { advanceState } from "../../gamestate/GameState";

const log = logging.getLogger("FinalRoundBoard");

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.85; text-shadow: 0 0 10px rgba(255,215,0,0.3); }
  50% { opacity: 1; text-shadow: 0 0 25px rgba(255,215,0,0.6), 0 0 50px rgba(255,215,0,0.2); }
`;

const Root = styled('div')({
  color: "white",
  width: "100%",
  height: "100%",
  minHeight: "100vh",
  textAlign: "center",
  fontFamily: "Gyparody",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
});

const StyledTypography = styled(Typography)({
  color: "white",
  width: "100%",
  textAlign: "center",
  fontFamily: "Gyparody",
});

const WagerBanner = styled(Typography)({
  fontFamily: "Gyparody",
  fontSize: "2em",
  color: "#FFD700",
  marginTop: "32px",
  animation: `${pulseGlow} 2.5s ease-in-out infinite`,
});

const FinalRoundBoard = () => {
  const dispatch = useDispatch();

  const gameId = useSelector(selectGameId);
  const gameState = useSelector(selectGameStatusObject);
  const gameObj = useSelector(selectGameObject);
  const players = useSelector(selectAllPlayers);
  const question = useSelector(selectCurrentQuestionObject);
  const questionId = gameState.questionId;

  // Watch submissions so FinalAllWagersIn trigger can detect when all wagers are in
  useEffect(() => {
    if (gameId && questionId) {
      dispatch(watchQuestionSubmissionsAction(gameId, questionId));
    }
    return () => {
      if (gameId && questionId) {
        dispatch(stopWatchingQuestionSubmissionsAction(gameId, questionId));
      }
    };
  }, [dispatch, gameId, questionId]);

  if (!question) {
    log.debug("No question found for final round");
    return <div />;
  }

  const isCategoryPreview =
    gameState.round === GAMESTATE_ROUND_FINAL &&
    (gameState.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_CATEGORY_PREVIEW ||
      gameState.roundState === GAMESTATE_ROUNDSTATE_FINALROUND_WAGER_INSTRUCTIONS);

  const isWagering =
    gameState.round === GAMESTATE_ROUND_FINAL &&
    gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_WAGERING;

  const showCategory = isCategoryPreview || isWagering;

  const openQuestion = async () => {
    await advanceState(gameObj, players, dispatch);
  };

  log.debug("Round: ", showCategory, "isCategoryPreview: ", isCategoryPreview);
  return (
    <Root>
      <StyledTypography variant="h1">
        Final
        <br />
        Round!
      </StyledTypography>
      <Fade timeout={1500} in={showCategory}>
        <FinalCategoryCell
          label={question.category}
          onClick={isCategoryPreview ? undefined : openQuestion}
        />
      </Fade>
      <Fade in={isWagering} timeout={800}>
        <WagerBanner>Place Your Wagers!</WagerBanner>
      </Fade>
    </Root>
  );
};

export { FinalRoundBoard };
